// =============================================================================
// BP-07-03: useResourceCategories Hook
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { getResourceCategories, getResourcesByCategory } from '@/features/resources/api/resourcesApi';
import type { ResourceCategory, PaginationParams } from '@/features/resources/types/resourcesTypes';
import { resourceQueryKeys } from '@/features/resources/types/resourcesTypes';

/**
 * Hook for fetching all resource categories
 */
export function useResourceCategories() {
  return useQuery({
    queryKey: resourceQueryKeys.categories,
    queryFn: getResourceCategories,
    staleTime: 30 * 60 * 1000, // 30 minutes - categories rarely change
  });
}

/**
 * Hook for fetching resources in a specific category
 */
export function useResourcesByCategory(
  categorySlug: string,
  pagination: PaginationParams = {},
  enabled = true
) {
  return useQuery({
    queryKey: [...resourceQueryKeys.category(categorySlug), pagination],
    queryFn: () => getResourcesByCategory(categorySlug, pagination),
    enabled: enabled && !!categorySlug,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Utility to build category tree from flat list
 */
export function buildCategoryTree(categories: ResourceCategory[]): ResourceCategory[] {
  const categoryMap = new Map<string, ResourceCategory>();
  const roots: ResourceCategory[] = [];

  // First pass: create map of all categories
  categories.forEach((cat) => {
    categoryMap.set(cat.id, { ...cat, children: [] });
  });

  // Second pass: build tree structure
  categories.forEach((cat) => {
    const category = categoryMap.get(cat.id)!;
    if (cat.parentId) {
      const parent = categoryMap.get(cat.parentId);
      if (parent) {
        parent.children = parent.children || [];
        parent.children.push(category);
      } else {
        roots.push(category);
      }
    } else {
      roots.push(category);
    }
  });

  // Sort by displayOrder
  const sortByOrder = (a: ResourceCategory, b: ResourceCategory) => 
    a.displayOrder - b.displayOrder;
  
  roots.sort(sortByOrder);
  roots.forEach((root) => {
    if (root.children) {
      root.children.sort(sortByOrder);
    }
  });

  return roots;
}

/**
 * Hook that returns categories as a tree structure
 */
export function useCategoryTree() {
  const query = useResourceCategories();

  return {
    ...query,
    data: query.data ? buildCategoryTree(query.data) : undefined,
  };
}

export default useResourceCategories;

/**
 * Resource Category Hook
 */
export function useResourceCategory(slug: string) {
  return {
    data: null,
    isLoading: false,
    error: null,
  };
}

/**
 * Category Resources Hook
 */
export function useCategoryResources(categorySlug: string) {
  return {
    data: [],
    isLoading: false,
    error: null,
    fetchNextPage: async () => {},
    hasNextPage: false,
  };
}

